import { PartnerWhiteLabelMetadata } from "../ClientPortalMetadata";
import {
  AdyenPaymentMethodType,
  AdyenPaymentMethodVerificationStatus,
  AdyenTransactionStatus,
  Transaction
} from "../Transaction";
import { Money } from "../AdyenSession";
import { CustomerMappingSource, CustomerOrigin } from "../Customer";
import { PaymentsIntegrationEnum } from "../Integration";

export interface Invoice {
  id: number;
  created_at: Date;
  updated_at: Date;
  invoice_number: string;
  collectible_invoice_id: string;
  status: InvoiceStatus;
  payment_is_processing: boolean;
  is_invoice_associated_with_chargeback: boolean;
  customer_id: number;
  customer_name: string;
  issue_date: Date;
  due_date: Date;
  total: number;
  surcharge_amount: number;
  amount_paid: number;
  balance: number;
  invoice_emails_enabled: boolean | null;
  payments_invoice_auto_collection_status: PaymentsInvoiceAutoCollectionStatus | null;
  auto_collection_scheduled_date: Date | null;
  payment_method: AdyenPaymentMethodType | null;
  last_sent_on: Date | null;
  adyen_transaction?: Transaction | null;
}

export interface InvoiceForClientPortalView {
  invoice_number: string;
  status: InvoiceStatus;
  payment_is_processing: boolean;
  is_invoice_associated_with_chargeback: boolean;
  total: number;
  issue_date: Date;
  due_date: Date;
  payments_invoice_auto_collection_status?: PaymentsInvoiceAutoCollectionStatus;
  customer_id: number;
  id: number;
  customer_name: string;
  payment_method?: AdyenPaymentMethodType;
  payment_method_summary?: string;
  auto_collection_scheduled_date?: Date;
  adyen_transaction?: Transaction;
}

export enum PaymentsInvoiceAutoCollectionStatus {
  Pending = "pending",
  Initiated = "initiated",
  Failed = "failed",
  Collected = "collected",
  Canceled = "canceled",
  AlreadyPaid = "already_paid"
}

export enum InvoiceStatus {
  Draft = "Draft",
  Open = "Open",
  Paid = "Paid",
  Overdue = "Overdue",
  Voided = "Voided"
}

export interface AvailablePaymentMethodView {
  active: boolean;
  verification_status: AdyenPaymentMethodVerificationStatus;
  type: AdyenPaymentMethodType;
}

export interface CheckoutDetailsView {
  invoice_status: InvoiceStatus;
  message?: string;
  payment_method: AvailablePaymentMethodView[];
  team_id: number;
  invoice_id?: number;
  customer_id?: number;
  partner_display_details: PartnerWhiteLabelMetadata;
  transaction_id?: number;
  transaction_status?: AdyenTransactionStatus;
}

export interface CollectNowResponse {
  invoice_id: number;
  message: string;
  type: string;
}

export interface Receipt {
  reference: string;
  date: string;
  payment_method: AdyenPaymentMethodType;
  payment_method_summary: string;
  amount: Money;
  surcharges_fee_amount: Money;
  original_amount: Money;
  surcharge_description: string;
  partner_display_details: PartnerWhiteLabelMetadata;
}

export interface AutoCollectDetail {
  invoice_number: string;
  out_standing_amount: string;
  customer_id: number;
  status: PaymentsInvoiceAutoCollectionStatus;
}

export function getCustomerCreationMethodLabel(
  origin: CustomerOrigin,
  mapping_source: CustomerMappingSource | undefined,
  connectedIntegration: PaymentsIntegrationEnum
): string {
  const sameOriginIntegration =
    (connectedIntegration === PaymentsIntegrationEnum.Quickbooks &&
      origin === CustomerOrigin.Quickbooks) ||
    (connectedIntegration === PaymentsIntegrationEnum.Xero && origin === CustomerOrigin.Xero);
  if (origin === CustomerOrigin.ZomentumGrow) {
    return "Auto Created";
  }
  if (sameOriginIntegration) {
    if (mapping_source === CustomerMappingSource.Auto) return "Auto Mapped";
    if (mapping_source === CustomerMappingSource.Manual) return "Mapped Manually";
    return "Imported";
  }
  const originStr = origin as string;
  const originLabel = originStr.charAt(0).toUpperCase() + originStr.slice(1);
  return `Imported from ${originLabel}`;
}
